import React from 'react';

/**
 * App-wide render guard.
 *
 * Before this, the SPA had no error boundary anywhere: a single render exception on
 * any screen unmounted the whole tree and left a blank white page with nothing to
 * explain it (the "clicked delete and everything disappeared" report). This catches
 * the error, keeps the rest of the app alive, and shows an actionable fallback.
 *
 * Mount it with `key={currentPage}` so navigating to another screen remounts it and
 * clears a crash — one broken page never wedges the whole app until a full reload.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught a render error:', error, info && info.componentStack);
  }

  handleReload = () => {
    try { window.location.reload(); } catch (e) { /* no-op */ }
  };

  render() {
    if (this.state.hasError) {
      const msg = this.state.error && this.state.error.message;
      return (
        <div className="flex min-h-[50vh] items-center justify-center p-6">
          <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-6 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-2xl font-bold text-red-600">!</div>
            <h2 className="text-lg font-semibold text-gray-900">Something went wrong on this screen</h2>
            <p className="mt-2 text-sm text-gray-500">
              The page hit an unexpected error and couldn’t finish loading. Your data is safe —
              nothing was changed by this error. Try reloading, or switch to another screen and back.
            </p>
            {msg && (
              <pre className="mt-3 max-h-24 overflow-auto rounded bg-gray-50 p-2 text-left text-xs text-gray-400">
                {String(msg)}
              </pre>
            )}
            <div className="mt-5 flex justify-center gap-3">
              <button
                type="button"
                onClick={this.handleReload}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
